// react
import React, { useCallback, useEffect, useReducer, useState } from 'react';

// third-party
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';

// application
// import CategorySidebar from './CategorySidebar';
// import CategorySidebarItem from './CategorySidebarItem';
import PageHeader from '../shared/PageHeader';
import ProductsView from './ProductsView';
import shopApi from '../../api/shop';
// import WidgetFilters from '../widgets/WidgetFilters';
// import WidgetProducts from '../widgets/WidgetProducts';
import { sidebarClose } from '../../store/sidebar';
import BlockKategori from "../blocks/BlockKategory";

// data stubs
import theme from '../../data/theme';
import { url, getCategoryParents, getCategoryById, getCategoryBySlug, slugify } from '../../services/utils';
import { useLocation } from 'react-router-dom';

function parseQueryOptions(location) {
    const query = queryString.parse(location);
    const optionValues = {};

    if (typeof query.page === 'string') {
        optionValues.page = parseFloat(query.page);
    }
    if (typeof query.limit === 'string') {
        optionValues.limit = parseFloat(query.limit);
    }
    if (typeof query.sort === 'string') {
        optionValues.sort = query.sort;
    }

    return optionValues;
}

function parseQueryFilters(location) {
    const query = queryString.parse(location);
    const filterValues = {};

    Object.keys(query).forEach((param) => {
        const mr = param.match(/^filter_([-_A-Za-z0-9]+)$/);

        if (!mr) {
            return;
        }

        const filterSlug = mr[1];

        filterValues[filterSlug] = query[param];
    });

    return filterValues;
}

function parseQuery(location) {
    return [
        parseQueryOptions(location),
        parseQueryFilters(location),
    ];
}

function buildQuery(options, filters) {
    const params = {};

    if (options.page !== 1) {
        params.page = options.page;
    }

    if (options.limit !== 12) {
        params.limit = options.limit;
    }

    if (options.sort !== 'default') {
        params.sort = options.sort;
    }

    Object.keys(filters).filter((x) => x !== 'category' && !!filters[x]).forEach((filterSlug) => {
        params[`filter_${filterSlug}`] = filters[filterSlug];
    });

    return queryString.stringify(params, { encode: false });
}

const initialState = {
    init: false,
    /**
     * Indicates that the category is loading.
     */
    categoryIsLoading: true,
    /**
     * Category object.
     */
    category: null,
    /**
     * Indicates that the products list is loading.
     */
    productsListIsLoading: true,
    /**
     * Products list array.
     */
    productsList: [],
    totalItem: 0,
    currentPage: 1,
    totalPage: 1,
    hasMore: true,
    isLoadingMore: false,
    /**
     * Products list options.
     */
    options: {},
    /**
     * Products list filters.
     */
    filters: {},
};

function reducer(state, action) {
    switch (action.type) {
    case 'FETCH_CATEGORY_SUCCESS':
        return {
            ...state,
            init: true,
            categoryIsLoading: false,
            category: action.category,
        };
    case 'FETCH_PRODUCTS_LIST_INIT':
        return {
            ...state,
            productsListIsLoading: true,
            productsList: [],
            totalItem: 0,
            currentPage: 1,
            totalPage: 1,
            hasMore: true,
            isLoadingMore: false,
        };
    case 'FETCH_PRODUCTS_LIST_SUCCESS': {
        const newItems = action.items || [];
        const isPageOne = action.page === 1;
        let merged;
        if (isPageOne) {
            merged = newItems;
        } else {
            const existingIds = new Set((state.productsList || []).map((x) => x.id));
            const uniqueNew = newItems.filter((x) => !existingIds.has(x.id));
            merged = [...(state.productsList || []), ...uniqueNew];
        }

        const totalItem = action.total_item != null ? action.total_item : (isPageOne ? newItems.length : state.totalItem);
        const totalPage = action.total_page != null ? action.total_page : 1;
        const hasMore = newItems.length > 0 && action.page < totalPage && (totalItem === 0 || merged.length < totalItem);

        return {
            ...state,
            productsListIsLoading: false,
            isLoadingMore: false,
            productsList: merged,
            totalItem: totalItem,
            currentPage: action.page,
            totalPage: totalPage,
            hasMore: hasMore,
        };
    }
    case 'FETCH_PRODUCTS_LIST_MORE':
        return {
            ...state,
            isLoadingMore: true,
        };
    case 'FETCH_PRODUCTS_LIST_ERROR':
        return {
            ...state,
            productsListIsLoading: false,
            isLoadingMore: false,
        };
    case 'SET_OPTION_VALUE':
        return {
            ...state,
            options: { ...state.options, page: 1, [action.option]: action.value },
        };
    case 'SET_FILTER_VALUE':
        return {
            ...state,
            options: { ...state.options, page: 1 },
            filters: { ...state.filters, [action.filter]: action.value },
        };
    case 'RESET_FILTERS':
        return { ...state, options: { ...state.options, page: 1 }, filters: {} };
    case 'RESET':
        return state.init ? initialState : state;
    default:
        throw new Error();
    }
}

function init(state) {
    const [options, filters] = parseQuery(window.location.search);

    return { ...state, options, filters };
}

function ShopPageCategory(props) {
    const {
        categorySlug,
        columns,
        viewMode,
        location,
        history,
    } = props;
    const offcanvas = columns === 3 ? 'mobile' : 'always';
    const [state, dispatch] = useReducer(reducer, initialState, init);
    const [latestProducts, setLatestProducts] = useState([]);

    const {search} = useLocation();
    const {name, wh_id} = queryString.parse(search);

    // Helper to resolve numeric category ID from state, numeric URL, storage, or slug map
    const resolveCategoryId = useCallback(() => {
        if (!categorySlug || categorySlug === 'all') return null;

        if (location?.state?.categoryId && location.state.categoryId !== 'all') {
            return location.state.categoryId;
        }

        if (/^\d+$/.test(categorySlug)) {
            return categorySlug;
        }

        if (typeof window !== 'undefined') {
            const cached =
                window.sessionStorage?.getItem(`cat_slug_${categorySlug}`) ||
                window.localStorage?.getItem(`cat_slug_${categorySlug}`);
            if (cached) return cached;
        }

        const mapped = getCategoryBySlug(categorySlug);
        if (mapped) return mapped.id;

        return categorySlug;
    }, [categorySlug, location]);

    const targetCategoryId = resolveCategoryId();

    // If accessed with 'all' or numeric ID (e.g. /shop/category/4), rewrite URL cleanly
    useEffect(() => {
        if (!categorySlug || !history) return;

        if (categorySlug === 'all') {
            history.replace({
                pathname: '/shop/category',
                state: {},
            });
            return;
        }

        let cleanSlug = null;
        let catId = targetCategoryId;

        if (/^\d+$/.test(categorySlug)) {
            const mapped = getCategoryById(categorySlug);
            if (mapped) {
                cleanSlug = mapped.slug;
                catId = mapped.id;
            }
        }

        if (cleanSlug && /^\d+$/.test(categorySlug)) {
            try {
                window.sessionStorage?.setItem(`cat_slug_${cleanSlug}`, String(catId));
                window.localStorage?.setItem(`cat_slug_${cleanSlug}`, String(catId));
            } catch (e) {}

            history.replace({
                pathname: `/shop/category/${cleanSlug}`,
                state: { categoryId: catId },
            });
        }
    }, [categorySlug, targetCategoryId, history]);

    // Load category.
    useEffect(() => {
        let request;
        let canceled = false;
        
        dispatch({ type: 'RESET', categorySlug });

        if (targetCategoryId) {
            request = shopApi.getCategoryBySlug(targetCategoryId);
        } else {
            request = Promise.resolve(null);
        }

        request.then((category) => {
            if (canceled) {
                return;
            }

            dispatch({ type: 'FETCH_CATEGORY_SUCCESS', category });
        });

        return () => {
            canceled = true;
        };
    }, [dispatch, targetCategoryId, categorySlug]);

    // Load products: initial 20 data for category
    useEffect(() => {
        let canceled = false;

        dispatch({ type: 'FETCH_PRODUCTS_LIST_INIT' });

        shopApi.getProductsList(targetCategoryId, { page: 1, per_page: 20 })
            .then((res) => {
                if (canceled) return;
                dispatch({
                    type: 'FETCH_PRODUCTS_LIST_SUCCESS',
                    items: res.items || [],
                    total_item: res.total_item,
                    total_page: res.total_page,
                    page: 1,
                });
            })
            .catch(() => {
                if (canceled) return;
                dispatch({ type: 'FETCH_PRODUCTS_LIST_ERROR' });
            });

        return () => {
            canceled = true;
        };
    }, [targetCategoryId]);

    // Lazy load next page (20 data)
    const loadMoreProducts = useCallback(() => {
        if (state.productsListIsLoading || state.isLoadingMore || !state.hasMore) {
            return;
        }

        const nextPage = (state.currentPage || 1) + 1;
        dispatch({ type: 'FETCH_PRODUCTS_LIST_MORE' });

        shopApi.getProductsList(targetCategoryId, { page: nextPage, per_page: 20 })
            .then((res) => {
                dispatch({
                    type: 'FETCH_PRODUCTS_LIST_SUCCESS',
                    items: res.items || [],
                    total_item: res.total_item,
                    total_page: res.total_page,
                    page: nextPage,
                });
            })
            .catch(() => {
                dispatch({ type: 'FETCH_PRODUCTS_LIST_ERROR' });
            });
    }, [targetCategoryId, state.productsListIsLoading, state.isLoadingMore, state.hasMore, state.currentPage]);

    // Load latest products.
    useEffect(() => {
        let canceled = false;

        if (offcanvas === 'always') {
            setLatestProducts([]);
        } else {
            shopApi.getLatestProducts({ limit: 5 }).then((result) => {
                if (canceled) {
                    return;
                }

                setLatestProducts(result);
            });
        }

        return () => {
            canceled = true;
        };
    }, [offcanvas]);

    const currentCategoryInfo = targetCategoryId ? (getCategoryById(targetCategoryId) || getCategoryBySlug(categorySlug)) : null;
    const categoryNameFromProduct = (targetCategoryId && state.productsList && state.productsList[0]) ? state.productsList[0].kategori_name : null;
    const resolvedCategoryName = targetCategoryId
        ? (currentCategoryInfo?.name || categoryNameFromProduct || (state.category && state.category.name))
        : null;

    const breadcrumb = [
        { title: 'Home', url: url.home() },
        { title: 'Kategori', url: url.catalog() },
    ];
    let pageTitle = resolvedCategoryName || 'Semua Kategori';
    let content;

    if (resolvedCategoryName) {
        breadcrumb.push({
            title: resolvedCategoryName,
            url: url.category(currentCategoryInfo || { id: targetCategoryId, name: resolvedCategoryName }),
        });
    } else {
        breadcrumb.push({
            title: 'Semua Kategori',
            url: url.catalog(),
        });
    }

    const productsView = (
        <ProductsView
            isLoading={state.productsListIsLoading}
            productsList={state.productsList}
            totalItem={state.totalItem}
            hasMore={state.hasMore}
            isLoadingMore={state.isLoadingMore}
            onLoadMore={loadMoreProducts}
            options={state.options}
            filters={state.filters}
            dispatch={dispatch}
            layout={viewMode}
            grid={`grid-${columns}-${columns > 3 ? 'full' : 'sidebar'}`}
            offcanvas={offcanvas}
        />
    );
    // const sidebarComponent = (
    //     <CategorySidebar offcanvas={offcanvas}>
    //         <CategorySidebarItem>
    //             <WidgetFilters
    //                 title="Filters"
    //                 offcanvas={offcanvas}
    //                 filters={name && wh_id ?[]: state.productsList.filters}
    //                 values={state.filters}
    //                 dispatch={dispatch}
    //             />
    //         </CategorySidebarItem>
    //         {offcanvas !== 'always' && (
    //             <CategorySidebarItem className="d-none d-lg-block">
    //                 <WidgetProducts title="Latest Products" products={latestProducts} />
    //             </CategorySidebarItem>
    //         )}
    //     </CategorySidebar>
    // );

    if (columns > 3) {
        content = (
            <React.Fragment>
                <BlockKategori />
                <div className="container">
                    <div className="block">{productsView}</div>
                    {/* {sidebarComponent} */}
                </div>
            </React.Fragment>
        );
    } else {
        // const sidebar = (
        //     <div className="shop-layout__sidebar">
        //         {/* {sidebarComponent} */}
        //     </div>
        // );

        content = (
            <div className="container">
                {/* <div className={`shop-layout shop-layout--sidebar--${sidebarPosition}`}>
                    {sidebarPosition === 'start' && sidebar} */}
                    <div className="shop-layout__content">
                        <div className="block">{productsView}</div>
                    </div>
                    {/* {sidebarPosition === 'end' && sidebar}
                </div> */}
            </div>
        );
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{`${pageTitle || 'Kategori Produk'} | ${theme.name}`}</title>
            </Helmet>

            <PageHeader breadcrumb={breadcrumb} />

            {content}
        </React.Fragment>
    );
}

ShopPageCategory.propTypes = {
    /**
     * Category slug.
     */
    categorySlug: PropTypes.string,
    /**
     * number of product columns (default: 3)
     */
    columns: PropTypes.number,
    /**
     * mode of viewing the list of products (default: 'grid')
     * one of ['grid', 'grid-with-features', 'list']
     */
    viewMode: PropTypes.oneOf(['grid', 'grid-with-features', 'list']),
    /**
     * sidebar position (default: 'start')
     * one of ['start', 'end']
     * for LTR scripts "start" is "left" and "end" is "right"
     */
    // sidebarPosition: PropTypes.oneOf(['start', 'end']),
};

ShopPageCategory.defaultProps = {
    columns: 4,
    viewMode: 'grid',
    // sidebarPosition: 'start',
};

const mapStateToProps = (state) => ({
    sidebarState: state.sidebar,
    page: state.category,
});

const mapDispatchToProps = () => ({
    sidebarClose,
});

export default connect(mapStateToProps, mapDispatchToProps)(ShopPageCategory);
